/**
 * A screen somebody's role does not reach.
 *
 * **The rule is that it is not there, not that it says no.** A form that renders, accepts what is
 * typed into it and then answers "you do not have permission to do that (property.edit)" has wasted
 * somebody's time and told them what to go looking for. Anything a role cannot do is removed: the
 * link, the button, and — because an address can always be typed, pasted or bookmarked — the route.
 *
 * The API still refuses. It has to: this is a convenience for the honest and nothing else, and the
 * only thing that actually decides is `orgContext(c, "property.edit")` on the server. What this
 * removes is the invitation.
 *
 * Redirects rather than showing an error: somebody who typed a URL they cannot use is sent to the
 * nearest screen they can, which is almost always the list the thing lives on.
 *
 * **It asks `mayReach`, not `can`.** A suspension is read-only, not a smaller application: a
 * suspended owner must still be able to open the screen that shows them what has happened. What
 * they cannot do is press anything on it, and that is `can`'s job.
 */

import { Navigate, useLocation } from "react-router-dom";
import type { Permission } from "@rentorng/core/permissions";
import { useSession } from "@/lib/session";
import { Spinner } from "@/components/ui";

export function Require({
  need,
  to,
  children,
}: {
  /** Every one of these is required. Most screens need only one. */
  need: Permission | Permission[];
  /** Where somebody without it is sent. The list the record lives on, as a rule. */
  to: string;
  children: React.ReactNode;
}) {
  const { mayReach, loading } = useSession();
  const location = useLocation();

  // The session decides this, so nothing is drawn until it has arrived — otherwise every guarded
  // screen flashes a redirect on a hard reload.
  if (loading) return <Spinner />;

  const needed = Array.isArray(need) ? need : [need];
  if (needed.every((permission) => mayReach(permission))) return <>{children}</>;

  return <Navigate to={to} replace state={{ deniedFrom: location.pathname }} />;
}
